'use client'

import type React from 'react'
import {
  Attachment,
  AttachmentInfo,
  AttachmentPreview,
  AttachmentRemove,
  Attachments,
  getMediaCategory,
} from '@/components/ai-elements/attachments'
import {
  PromptInputHeader,
  PromptInputSubmit,
  usePromptInputAttachments,
} from '@/components/ai-elements/prompt-input'
import { useOptionalCan } from '@/hooks/use-can'

export function ComposerAttachments() {
  const { files, remove } = usePromptInputAttachments()
  if (!files.length) {
    return null
  }

  const getAttachmentDescription = (
    file: (typeof files)[number]
  ): string | undefined => {
    if (file.uploadStatus === 'uploading') {
      return 'Uploading to Blob...'
    }
    if (file.uploadStatus === 'error') {
      return file.uploadError ?? 'Upload failed. Remove and try again.'
    }
    return file.previewText
  }

  return (
    <PromptInputHeader className="px-3 pt-3">
      <Attachments variant="inline">
        {files.map((file) => (
          <Attachment
            key={file.id}
            data={file}
            onRemove={() => remove(file.id)}
          >
            <AttachmentPreview />
            {getMediaCategory(file) !== 'image' ? (
              <AttachmentInfo
                showMediaType
                description={getAttachmentDescription(file)}
              />
            ) : null}
            <AttachmentRemove />
          </Attachment>
        ))}
      </Attachments>
    </PromptInputHeader>
  )
}

export function ComposerSubmitButton({
  input,
  status,
  onStop,
  allowStop = true,
  disabled = false,
  hasImplicitContent = false,
}: {
  input: string
  status?: React.ComponentProps<typeof PromptInputSubmit>['status']
  onStop?: () => void
  allowStop?: boolean
  disabled?: boolean
  /**
   * When true, treat the composer as having content even if the textarea is
   * empty and no files are attached. Used by the templates remix page where
   * the source-email chip is the implicit task.
   */
  hasImplicitContent?: boolean
}) {
  const { files, isUploading, hasUploadError } = usePromptInputAttachments()
  // Chatting spends credits and edits the brand: viewers only look. Public
  // surfaces have no org context and are not gated here.
  const canWork = useOptionalCan('chat.run') ?? true
  const hasContent =
    hasImplicitContent || input.trim().length > 0 || files.length > 0
  const isStreaming = status === 'streaming'
  const submitStatus = isStreaming && !allowStop ? 'submitted' : status
  const effectiveOnStop = allowStop ? onStop : undefined
  // Stoppable turns must stay clickable even though the composer is
  // empty and `disabled` is set — the button is a Stop button then, and
  // the content/upload gates only apply to sending.
  const isStoppable =
    typeof effectiveOnStop === 'function' &&
    (isStreaming || submitStatus === 'submitted')

  return (
    <PromptInputSubmit
      disabled={
        !isStreaming &&
        !isStoppable &&
        (disabled || !canWork || !hasContent || isUploading || hasUploadError)
      }
      onStop={effectiveOnStop}
      status={submitStatus}
      data-testid="submit-button"
    />
  )
}
